import React, {useEffect, useState, useRef, useMemo} from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
  Paper,
  Divider,
} from '@mui/material';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/system';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

const StyledCardMedia = styled(CardMedia)({
  height: 0,
  paddingTop: '56.25%',
});

const MenuItem = (props) => {
  // Thumbnail for card listing (performance)
  const [thumbnailSrc, setThumbnailSrc] = useState(() => {
    if (!props.img) return require('../Images/MenuImgs/NoImage.jpeg');
    
    // Try thumbnail first
    const fileNameWithoutExt = props.img.split('.')[0];
    return `https://storage.googleapis.com/menu_library/${fileNameWithoutExt}_thumbnail.webp`;
  });
  
  // Full image for dialog (quality)
  const fullImgSrc = props.img ? `https://storage.googleapis.com/menu_library/${props.img}` : require('../Images/MenuImgs/NoImage.jpeg');
  
  const [thumbnailError, setThumbnailError] = useState(false);
  
  // For video
  const videoRef = useRef(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [videoAvailable, setVideoAvailable] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  
  const [errorMsg, setErrorMsg] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedExtras, setSelectedExtras] = useState({});
  const [activeStates, setActiveStates] = useState([]); // derived from selected options that include a `state`

  // Initialize default options when dialog opens
  const initializeDefaultOptions = () => {
    const defaultOptions = {};
    
    // Select the first option of each option kind by default
    props.optionKinds.forEach(optionKind => {
      if (optionKind.options && optionKind.options.length > 0) {
        defaultOptions[optionKind.id] = optionKind.options[0];
      }
    });
    
    setSelectedOptions(defaultOptions);
    setActiveStates(computeActiveStates(defaultOptions));
  };

  // Compute active states from selected options
  const computeActiveStates = (optMap) => {
    const states = [];
    for (const okId in optMap) {
      const opt = optMap[okId];
      if (opt && opt.state) {
        states.push(opt.state);
      }
    }
    return states;
  };

  // Visibility helpers for optionKinds and extrasKinds
  const normalizeShowWhen = (showWhen) => {
    if (!showWhen) return [];
    return Array.isArray(showWhen) ? showWhen : [showWhen];
  };

  const isBlockVisible = (block, states) => {
    const req = normalizeShowWhen(block.showWhen);
    if (req.length === 0) return true; // no condition => always visible
    // visible if any required state is active
    return req.some(r => states.includes(r));
  };

  const getVisibleOptionKinds = () => {
    return (props.optionKinds || []).filter(ok => isBlockVisible(ok, activeStates));
  };

  const getVisibleExtrasKinds = () => {
    return (props.extrasKinds || []).filter(ek => isBlockVisible(ek, activeStates));
  };

  // When visibility changes:
  // - Options: clear hidden kinds, and ensure each visible kind has a default (first) selection
  // - Extras: clear hidden kinds (no defaults for extras)
  useEffect(() => {
    const visibleOptionKinds = getVisibleOptionKinds();
    const visibleOptionIds = new Set(visibleOptionKinds.map(k => parseInt(k.id)));

    // Start from existing, keep only visible option kinds
    const nextOptions = {};
    for (const okId in selectedOptions) {
      if (visibleOptionIds.has(parseInt(okId))) {
        nextOptions[okId] = selectedOptions[okId];
      }
    }

    // Add default (first option) for any visible kind missing a selection
    for (const ok of visibleOptionKinds) {
      const key = String(ok.id);
      if (!nextOptions.hasOwnProperty(key) && ok.options && ok.options.length > 0) {
        nextOptions[key] = ok.options[0];
      }
    }

    // Apply if changed and recompute active states
    const optionsChanged = Object.keys(nextOptions).length !== Object.keys(selectedOptions).length 
      || Object.keys(nextOptions).some(k => nextOptions[k]?.id !== selectedOptions[k]?.id);
    if (optionsChanged) {
      setSelectedOptions(nextOptions);
      setActiveStates(computeActiveStates(nextOptions));
    }

    // Clear hidden extras kinds
    const visibleExtraIds = new Set(getVisibleExtrasKinds().map(k => parseInt(k.id)));
    const prunedExtras = {};
    for (const ekId in selectedExtras) {
      if (visibleExtraIds.has(parseInt(ekId))) {
        prunedExtras[ekId] = selectedExtras[ekId];
      }
    }
    if (Object.keys(prunedExtras).length !== Object.keys(selectedExtras).length) {
      setSelectedExtras(prunedExtras);
    }
  }, [activeStates]);

  // Check if video exists - using same name as image but with webm extension
  const checkVideoAvailability = async () => {
    if (!props.img) {
      setVideoAvailable(false);
      setVideoLoading(false);
      return;
    }
    
    setVideoLoading(true);
    
    try {
      // Extract the filename without extension
      const imgFileName = props.img.split('.')[0];
      //const videoUrl = `https://storage.googleapis.com/tas-images/MenuImgs/${imgFileName}.webm`;
      const videoUrl = `https://storage.googleapis.com/menu_library/${imgFileName}.webm`;
      
      // Use HEAD request to check if video exists
      const response = await fetch(videoUrl, { method: 'HEAD' });
      
      if (response.ok) {
        setVideoSrc(videoUrl);
        setVideoAvailable(true);
      } else {
        throw new Error('Video not found');
      }
    } catch (error) {
      console.log('Video not available', error);
      setVideoAvailable(false);
    } finally {
      setVideoLoading(false);
    }
  };

  // Handle dialog open - start checking for video and initialize default options
  const handleClickOpen = () => {
    // Reset video states before opening dialog
    setVideoLoading(true);
    setVideoError(false);
    setVideoAvailable(false);
    setVideoSrc(null);
    
    // Initialize default options
    initializeDefaultOptions();
    
    setOpen(true);
    checkVideoAvailability();
  };

  const handleClose = () => {
    setOpen(false);
    // Pause the video when dialog closes
    if (videoRef.current) {
      videoRef.current.pause();
    }
    // Reset loading states when dialog closes
    setVideoLoading(true);
    setVideoError(false);
  };

  // Video loading management
  useEffect(() => {
    if (open && videoRef.current && videoSrc && videoAvailable) {
      // Set up video event handlers
      const handleCanPlay = () => {
        setVideoLoading(false);
      };
      
      const handleError = () => {
        setVideoError(true);
        setVideoLoading(false);
      };
      
      // Add event listeners
      videoRef.current.addEventListener('canplay', handleCanPlay);
      videoRef.current.addEventListener('error', handleError);
      
      // Reset video element
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      
      // Set the source and start loading
      videoRef.current.src = videoSrc;
      videoRef.current.load();
      
      return () => {
        // Clean up event listeners
        if (videoRef.current) {
          videoRef.current.removeEventListener('canplay', handleCanPlay);
          videoRef.current.removeEventListener('error', handleError);
        }
      };
    }
  }, [open, videoSrc, videoAvailable]);

  const handleOptionChange = (optionKindId, option) => {
    const next = { ...selectedOptions, [optionKindId]: option };
    setSelectedOptions(next);
    setActiveStates(computeActiveStates(next));
  };

  const handleExtraChange = (extraKindId, extraId) => {
    const newExtras = { ...selectedExtras };
    if (newExtras[extraKindId]?.includes(extraId)) {
      newExtras[extraKindId] = newExtras[extraKindId].filter((id) => id !== extraId);
    } else {
      newExtras[extraKindId] = [...(newExtras[extraKindId] || []), extraId];
    }
    setSelectedExtras(newExtras);
  };

  const getSelectedExtraObjects = (selectedExtras, extrasKinds) => {
    const selectedExtraObjects = [];

    for (const extraKindId in selectedExtras) {
      selectedExtras[extraKindId].forEach((extraId) => {
        // Find extras kind by id (do not assume array index === id)
        const ek = (extrasKinds || []).find((k) => k.id === parseInt(extraKindId));
        const extra = ek?.extras?.find((e) => e.id === extraId);
        if (extra) {
          selectedExtraObjects.push({
            ...extra,
            extraKindId,
            extraKindName: ek?.name,
          });
        }
      });
    }

    return selectedExtraObjects;
  };

  const handleSubmit = () => {
    const item = {
      itemId: props.itemId,
      title: props.title,
      basePrice: props.price,
      img: props.img,
      sectionId: props.sectionId,
      options: [],
      extras: [],
      totalItemPrice: 0,
    };

    // Only require option kinds that are currently visible
    const visibleOptionKinds = getVisibleOptionKinds();
    const missingOptionKinds = visibleOptionKinds.filter((optionKind) => !selectedOptions.hasOwnProperty(optionKind.id));

    if (missingOptionKinds.length > 0) {
      setErrorMsg(`Husk at vælge: ${missingOptionKinds.map((optionKind) => optionKind.name).join(', ')}`);
      return;
    } else {
      setErrorMsg('');
    }

    const visibleOptionKindsForSubmit = getVisibleOptionKinds();
    for (const optionKindId in selectedOptions) {
      const ok = visibleOptionKindsForSubmit.find((k) => k.id === parseInt(optionKindId));
      if (!ok) continue;
      const option = selectedOptions[optionKindId];
      const optionKind = props.optionKinds.find((optionKind) => optionKind.id === parseInt(optionKindId));

      item.options.push({
        optionKindId: optionKindId,
        optionKindName: optionKind?.name,
        optionId: option.id,
        optionTitle: option.title,
        optionPrice: parseFloat(option.price),
      });
    }

    const selectedExtraObjects = getSelectedExtraObjects(selectedExtras, props.extrasKinds);
    selectedExtraObjects.forEach((extra) => {
      item.extras.push({
        extraKindId: extra.extraKindId,
        extraKindName: extra.extraKindName,
        extraId: extra.id,
        extraTitle: extra.title,
        extraPrice: parseFloat(extra.price),
      });
    });

    item.totalItemPrice = calculateTotalItemPrice();

    props.onClickFood(item.totalItemPrice, item);
    handleClose();
  };

  const calculateTotalItemPrice = () => {
    let totalItemPrice = props.price;
    // Respect visibility: only count options from currently visible kinds
    const visibleOptionKinds = getVisibleOptionKinds();
    for (const optionKindId in selectedOptions) {
      const ok = visibleOptionKinds.find((k) => k.id === parseInt(optionKindId));
      if (!ok) continue;
      const selectedOption = selectedOptions[optionKindId];
      totalItemPrice += parseFloat(selectedOption.price);
    }

    for (const extraKindId in selectedExtras) {
      const extras = selectedExtras[extraKindId];
      // Respect visibility: only count extras from currently visible kinds
      const ek = getVisibleExtrasKinds().find((k) => k.id === parseInt(extraKindId));
      if (!ek) continue;
      extras.forEach((extraId) => {
        const extra = ek.extras.find((extra) => extra.id === extraId);
        if (extra) totalItemPrice += parseFloat(extra.price);
      });
    }

    return totalItemPrice;
  };

  // Live total for button display
  const liveTotalPrice = useMemo(() => calculateTotalItemPrice(), [selectedOptions, selectedExtras, activeStates, props.price]);

  return (
    <>
      <Card onClick={handleClickOpen} sx={{ height: { xs: 'fit-content', md: '12vh' }, display: 'flex', marginBottom: '10px', padding: '20px', paddingLeft: '0px', paddingRight: { xs: '0px', md: '20px' }, paddingBottom: { xs: '0px', md: '20px' }, paddingTop: '0px', boxShadow: 'none', borderTop: '1px #e4e4e4 solid', borderRadius: '0' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <CardContent sx={{ flex: '1 0 auto', paddingLeft: { xs: '0px', md: '20px' } }}>
            <Typography component="div" variant="p" sx={{ 
              fontFeatureSettings: 'kern', 
              WebkitFontSmoothing: 'antialiased', 
              textRendering: 'optimizeLegibility', 
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, Roboto, "Open Sans", "Helvetica Neue", sans-serif', 
              fontSize: '1rem' 
            }}>
              {props.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div" sx={{ marginBottom: '5px', marginTop: '5px', 
              fontFeatureSettings: 'kern', 
              WebkitFontSmoothing: 'antialiased', 
              textRendering: 'optimizeLegibility', 
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, Roboto, "Open Sans", "Helvetica Neue", sans-serif', 
              fontSize: '1rem' 
            }}>
              {props.description}
            </Typography>
            <Typography className="font-link" sx={{color:"#009de0", fontWeight:"bold"}} variant="body2" color="text.secondary" component="div">
              {props.price} kr
            </Typography>
          </CardContent>
        </Box>
        <CardMedia
          component="img"
          sx={{ width: { xs: 120, md: 'auto' }, height: { xs: '120%', md: '100%' }, borderRadius: '4px', marginLeft: 'auto' }}
          image={thumbnailSrc}
          alt={`bedste ${props.sectionTitle} i ${props.city}`}
          onError={() => {
            if (!thumbnailError && props.img) {
              // Fallback to original image if thumbnail fails
              setThumbnailError(true);
              setThumbnailSrc(`https://storage.googleapis.com/menu_library/${props.img}`);
            }
          }}
        />
      </Card>

      {/* FIXED DIALOG COMPONENT WITH RESPONSIVE STYLING */}
      <Dialog open={open} onClose={handleClose} sx={{
          '& .MuiPaper-root': {
            borderRadius: '16px',
            maxHeight: { xs: '90vh', sm: '80vh' },
            overflowY: 'auto',
          },
        }}>
        <DialogContent>
          <Grid container alignItems="center">
            <Grid item xs={12} sx={{ position: 'relative', width: '100%' }}>
              {/* Video element */}
              {videoAvailable && (
                <>
                  {videoLoading && (
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.1)',
                      }}
                    >
                      <Typography variant="body2">Loading video...</Typography>
                    </Box>
                  )}
                  <video
                    ref={videoRef}
                    style={{ 
                      width: "100%", 
                      display: videoLoading ? 'none' : 'block',
                      objectFit: "contain", 
                      borderRadius: "8px",
                      maxHeight: "40vh"
                    }}
                    autoPlay
                    muted
                    playsInline
                    loop
                  />
                </>
              )}
              
              {/* Fallback to image if video has error or no video available */}
              {(!videoAvailable || videoError) && (
                <img
                  src={fullImgSrc}
                  alt={`bedste ${props.sectionTitle} i ${props.city}`}
                  style={{ 
                    width: "100%", 
                    objectFit: "contain", 
                    borderRadius: "8px",
                    maxHeight: "40vh"
                  }}
                />
              )}
            </Grid>
            <Grid item xs={8}>
              <Typography sx={{
                fontFeatureSettings: '"kern", "ss01", "ss05", "ss07"',
                WebkitFontSmoothing: 'antialiased',
                textRendering: 'optimizeLegibility',
                fontFamily: 'WoltHeading-Omnes, system-ui, -apple-system, BlinkMacSystemFont, Roboto, "Open Sans", sans-serif',
                fontVariantLigatures: 'common-ligatures',
                fontSize: {xs:"1.3rem",sm:'1.7rem'},
                fontWeight: 600,
                fontStyle: 'normal',
                fontStretch: 'normal',
                textTransform: 'none'
              }}>
                {props.title}
              </Typography>
            </Grid>
          </Grid>

          <Typography className="font-link" sx={{color:"#009de0", fontWeight:"bold",fontSize:"1.2rem",marginTop:"2%",marginBottom:"2%"}} variant="body2" color="text.secondary" component="div">
            {props.price} kr
          </Typography>
          <Typography variant="body1" sx={{paddingBottom:"7%",fontSize:"1.2rem"}}>{props.description}</Typography>
          
          {/* Options */}
          {getVisibleOptionKinds().map((optionKind, okIdx) => (
            <Box key={optionKind.id} sx={{ mt: okIdx === 0 ? 2 : 0 }}>
              {okIdx > 0 && <Divider sx={{ borderColor: '#eee', mb: 1 }} />}
              <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.2, fontSize: '0.9rem', lineHeight: 1.1 }}>{optionKind.name}</FormLabel>
              <RadioGroup
                // Show a visible default immediately: use first option id if none selected yet
                value={
                  selectedOptions[optionKind.id]?.id != null
                    ? String(selectedOptions[optionKind.id]?.id)
                    : (optionKind.options && optionKind.options.length > 0
                        ? String(optionKind.options[0].id)
                        : undefined)
                }
                onChange={(e) => {
                  const raw = e.target.value;
                  const selectedOptionId = isNaN(Number(raw)) ? raw : parseInt(raw);
                  const selectedOption = optionKind.options.find((option) => option.id === selectedOptionId);
                  handleOptionChange(optionKind.id, selectedOption);
                }}
              >
                {optionKind.options.map((option) => (
                  <Box
                    key={option.id}
                    sx={{ py: 0.25, cursor: 'pointer', borderRadius: 1, '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' }, '&:active': { backgroundColor: 'rgba(0,0,0,0.05)' } }}
                    onClick={(e) => {
                      const el = e.target;
                      if (el && (el.closest && (el.closest('input') || el.closest('.MuiRadio-root') || el.closest('.MuiCheckbox-root')))) return;
                      handleOptionChange(optionKind.id, option);
                    }}
                    role="button"
                    aria-label={`Vælg ${option.title}`}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <FormControlLabel
                        value={String(option.id)}
                        control={
                          <Radio
                            size="small"
                            icon={<RadioButtonUncheckedIcon fontSize="small" />}
                            checkedIcon={<RadioButtonCheckedIcon fontSize="small" />}
                            sx={{ color: '#009de0', '&.Mui-checked': { color: '#009de0' }, '& .MuiSvgIcon-root': { fontSize: 18 } }}
                          />
                        }
                        label=""
                        sx={{ m: 0, minHeight: 28, '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', pl: 0.5 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: '0.95rem', lineHeight: 1.1 }}>{option.title}</Typography>
                        {parseFloat(option.price) ? (
                          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>+ {option.price} kr</Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </RadioGroup>
              </FormControl>
            </Box>
          ))}
          
          {/* Extras */}
          {getVisibleExtrasKinds().map((extraKind, ekIdx) => (
            <Box key={extraKind.id} sx={{ mt: ekIdx === 0 ? 2 : 0 }}>
              {ekIdx > 0 && <Divider sx={{ borderColor: '#eee', mb: 1 }} />}
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.2, fontSize: '0.9rem', lineHeight: 1.1 }}>{extraKind.name}</FormLabel>
                <FormGroup>
                {extraKind.extras.map((extra) => (
                  <Box
                    key={extra.id}
                    sx={{ py: 0.25, cursor: 'pointer', borderRadius: 1, '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' }, '&:active': { backgroundColor: 'rgba(0,0,0,0.05)' } }}
                    onClick={(e) => {
                      const el = e.target;
                      if (el && (el.closest && (el.closest('input') || el.closest('.MuiRadio-root') || el.closest('.MuiCheckbox-root')))) return;
                      handleExtraChange(extraKind.id, extra.id);
                    }}
                    role="button"
                    aria-label={`Tilføj ${extra.title}`}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <FormControlLabel
                        sx={{ m: 0, minHeight: 28, '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }}
                        control={
                          <Checkbox
                            size="small"
                            sx={{ color: '#009de0', '&.Mui-checked': { color: '#009de0' }, '& .MuiSvgIcon-root': { fontSize: 20 } }}
                            checked={selectedExtras[extraKind.id]?.includes(extra.id) || false}
                            onChange={() => handleExtraChange(extraKind.id, extra.id)}
                          />
                        }
                        label=""
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', pl: 0.5 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: '0.95rem', lineHeight: 1.1 }}>{extra.title}</Typography>
                        {parseFloat(extra.price) ? (
                          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>+ {extra.price} kr</Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                ))}
                </FormGroup>
              </FormControl>
            </Box>
          ))}
        </DialogContent>
        
        <DialogActions
          sx={{
            backgroundColor: "transparent",
            display:"block",
          }}
        >
          <IconButton
            edge="end"
            onClick={handleClose}
            aria-label="close"
            sx={{
              color: 'black',
              backgroundColor: 'rgba(23, 15, 15, 0.3)',
              position: "absolute",
              top: "2%",
              right: "6%",
              width: "fit-content",
            }}
          >
            <CloseIcon />
          </IconButton>

          {errorMsg && <Paper className="font-link" style={{ backgroundColor: '#f44336', color: 'white', padding: '1rem', borderRadius: '4px', textAlign: 'center' }}>{errorMsg}</Paper>}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              backgroundColor: "transparent",
            }}
          >
            <Button
              onClick={handleSubmit}
              color="primary"
              sx={{
                backgroundColor: "#009de0",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, Roboto, "Open Sans", "Helvetica Neue", sans-serif',
                fontSize: '1rem',
                fontWeight: 'normal',
                lineHeight: 'normal',
                letterSpacing: 'normal',
                fontFeatureSettings: '"kern"',
                WebkitFontSmoothing: 'antialiased',
                textRendering: 'optimizeLegibility',
                height: '50px',
                width: '90%',
                color: "white",
                fontWeight: "bold",
                borderRadius: "10px",
                padding: "15px",
                margin: "10px",
              }}
            >
              <Box>Add to Order</Box>
              <Box>{liveTotalPrice} kr</Box>
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MenuItem;
